import React, { memo, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import {
  getAbsolutePlayerTimePure,
  getPlayerTimeFromAbsoluteTimePure
} from '@goldcast/player-config/src/utils/timeUtils';
import {
  getAllSegments,
  getIsDuplicateRecording,
  isClipInOriginalLanguage,
  isFullLengthClip,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { getPlayerConfigElements } from '@goldcast/player-config/src/utils/playerConfigGetters';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { VideoSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/VideoSegment';
import { setZoomValue, calculateZoomMultipliers, zoomLevelStore } from './store/zoomLevel';
import { AddSegmentType } from './IntroOutroPopover/types';
import IntroOutroPopover from './IntroOutroPopover';
import IntroOutroSegment from './TimelineSegments/IntroOutroSegment';
import TimelineCompositeSegment from './TimelineSegments/CompositeSegment';
import TimelineTrackElementLanes from './TimelineTrackElementLanes/TimelineTrackElementLanes';
import CuePoint from './CuePoint';
import TimelineChapter from './TimelineChapter';
import SegmentGapActions from './TimelineSegments/SegmentGap/SegmentGapActions';
import { useTimelineZoomAdjustment } from './hooks/useAutoAdjustZoom';
import HoverPoint from './HoverPoint';
import { getSegmentAtCurrentFrame, getSegmentTimeFromAbsoluteTime, MIN_SEGMENT_WIDTH } from './timeUtils';
import {
  getSegmentTransitionBoundaryIds,
  getSegmentTransitionBoundaryKey,
  getSegmentTransitionState
} from './clipUpdateDataUtilsWithSplits/segmentTransitionUtils';
import VideoSegmentComponent from './TimelineSegments/VideoSegmentComponent';
import FullTimelineSelect from './TimelineSegments/FullTimelineSelect';
import SplitsIntroOutroSegment from './TimelineSegments/SplitsIntroOutroSegment';
import { useChapterAutoAdjust } from './hooks/useChapterAutoAdjust';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { isSplitsFeatureEnabled } from '@/stores/clip';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

const SpeakerTimeline = ({
  newTimelineContainerRef,
  isDisabled = false
}: {
  newTimelineContainerRef: React.RefObject<HTMLDivElement>;
  isDisabled?: boolean;
}) => {
  const speakerTimelineRef = useRef<HTMLDivElement>(null);
  const timelineSubTrackRef = useRef<HTMLDivElement>(null);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isSegmentTransitionsEnabled = featureFlags[FeatureFlagKeys.Use_CL_Segment_Transitions];
  const isChaptersInMenuEnabled = featureFlags[FeatureFlagKeys.Use_CL_Chapters_In_Menu];

  const { seekTo } = useRemotionPlayerSeek();
  const { clipData, fps } = useClipsContext();
  const isSplitsFFEnabled = isSplitsFeatureEnabled(featureFlags, clipData);
  const speakerTimelineContainerRef = useRef<HTMLDivElement>(null);
  const [compositeSegmentDragStart, setCompositeSegmentDragStart] = useState(0);
  const [compositeSegmentDragEnd, setCompositeSegmentDragEnd] = useState(0);
  const [compositeSegmentDragId, setCompositeSegmentDragId] = useState<string | null>(null);
  const isDraggingTimelineEntityRef = useRef(false);
  const [isDraggingTimelineEntity, setIsDraggingTimelineEntity] = useState(false);
  const chaptersContainerRef = useRef<HTMLDivElement>(null);
  const compositeTimelineRef = useRef<HTMLDivElement>(null);
  const speakerTimeLineFixedWidthContainer = useRef<HTMLDivElement>(null);

  const hasElements = useMemo(() => {
    const elementsTrack = getPlayerConfigElements(clipData?.asset_metadata?.player_config);
    return elementsTrack?.length > 0;
  }, [clipData?.asset_metadata?.player_config]);

  const isTranslated = useMemo(() => {
    return !isClipInOriginalLanguage(clipData);
  }, [clipData]);

  useTimelineZoomAdjustment({ speakerTimelineContainerRef: speakerTimeLineFixedWidthContainer });
  useChapterAutoAdjust();
  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!speakerTimelineRef.current || isDraggingTimelineEntityRef.current) {
      return;
    }
    const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;

    const timelineRect = speakerTimelineRef.current.getBoundingClientRect();
    // Get the mouse click position with respect to the timeline element
    if (
      e.target !== speakerTimelineContainerRef.current &&
      !speakerTimelineContainerRef.current?.contains(e.target as Node)
    ) {
      return;
    }

    const clickPositionX = e.clientX - timelineRect.left;
    const clickTimeInSeconds = clickPositionX / zoomLevel / fps;
    const time = getPlayerTimeFromAbsoluteTimePure({
      absoluteTime: clickTimeInSeconds,
      clipData: clipData,
      minSegmentWidth: MIN_SEGMENT_WIDTH,
      zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
    const currentSegment = getSegmentAtCurrentFrame(time, clipData);
    if (currentSegment) {
      const segmentTime = getSegmentTimeFromAbsoluteTime(clickTimeInSeconds, clipData, currentSegment.id);
      EventBus.dispatch(CustomEvents.TimelineForcedScroll, {
        time: segmentTime,
        segmentId: currentSegment.id
      });
      seekTo(time);
    }
  };

  useEffect(() => {
    const newTimelineEntityDraggingStateListener = EventBus.on(
      CustomEvents.NewTimelineEntityDraggingStateChanged,
      ({ isDraggingTimelineEntity }) => {
        isDraggingTimelineEntityRef.current = isDraggingTimelineEntity;
        setIsDraggingTimelineEntity(isDraggingTimelineEntity);
      }
    );

    return () => {
      EventBus.off(CustomEvents.NewTimelineEntityDraggingStateChanged, newTimelineEntityDraggingStateListener);
    };
  }, []);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
        e.stopPropagation();
        if (
          speakerTimeLineFixedWidthContainer.current &&
          !speakerTimeLineFixedWidthContainer.current.contains(e.target as Node)
        ) {
          return;
        }

        const currentZoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
        const currentBaseZoomLevel = zoomLevelStore.getSnapshot().baseZoomLevel;
        const zoomFactor = 1 - e.deltaY * 0.005;

        const zoomMultipliers = calculateZoomMultipliers(currentBaseZoomLevel);
        const minZoomLevel = zoomMultipliers[0] * currentBaseZoomLevel;
        const maxZoomLevel = zoomMultipliers[zoomMultipliers.length - 1] * currentBaseZoomLevel;
        const timelineRect = speakerTimelineContainerRef.current?.getBoundingClientRect();

        if (!timelineRect) {
          return;
        }

        let newZoomLevel = currentZoomLevel * zoomFactor;
        newZoomLevel = Math.max(minZoomLevel, Math.min(maxZoomLevel, newZoomLevel));
        const mouseX = e.clientX - timelineRect.left - 11;

        const hoverTime = getPlayerTimeFromAbsoluteTimePure({
          absoluteTime: mouseX / currentZoomLevel / fps,
          clipData: clipData,
          minSegmentWidth: MIN_SEGMENT_WIDTH,
          zoomLevel: zoomLevelStore.getSnapshot().zoomLevel,
          isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
        });
        setZoomValue(newZoomLevel);
        const newHoverLocation =
          getAbsolutePlayerTimePure({
            time: hoverTime,
            clipData: clipData,
            minSegmentWidth: MIN_SEGMENT_WIDTH,
            zoomLevel: newZoomLevel,
            isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
          }) *
          fps *
          newZoomLevel;
        const scrollOffset = newHoverLocation - mouseX;

        if (speakerTimelineContainerRef.current) {
          const mouseMoveEvent = new MouseEvent('mousemove', {
            clientX: e.clientX,
            clientY: e.clientY,
            bubbles: true,
            cancelable: true
          });
          if (speakerTimeLineFixedWidthContainer.current) {
            speakerTimeLineFixedWidthContainer.current.scrollLeft += scrollOffset;
          }
          speakerTimelineContainerRef.current.dispatchEvent(mouseMoveEvent);
        }
        return;
      }
    };

    const timelineContainer = speakerTimeLineFixedWidthContainer.current;
    timelineContainer?.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      timelineContainer?.removeEventListener('wheel', handleWheel);
    };
  }, [speakerTimelineRef, fps, clipData]);

  const isSplitsEnabled = useMemo(() => {
    return isSplitsInTimelineEnabledPure({
      clip: clipData,
      isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
  }, [clipData]);

  const isChaptersEnabled = useMemo(() => {
    return isFullLengthClip(clipData) && !getIsDuplicateRecording(clipData);
  }, [clipData]);

  const videoElementHeight = useMemo(
    () => compositeTimelineRef?.current?.offsetHeight ?? 0,
    [compositeTimelineRef?.current?.offsetHeight]
  );

  const introOutroPopoverStyle = useMemo(() => {
    const subTrackHeight = (hasElements && timelineSubTrackRef.current?.offsetHeight) || 0;
    const chapterHeight = isChaptersEnabled ? 32 : 0;
    const totalMarginTop = subTrackHeight + chapterHeight;
    const speakerTimelineHeight = speakerTimelineRef.current?.offsetHeight ?? 0;

    return {
      marginTop: `${totalMarginTop}px`,
      height: `${speakerTimelineHeight}px`
    };
  }, [
    hasElements,
    isChaptersEnabled,
    timelineSubTrackRef.current?.offsetHeight,
    speakerTimelineRef.current?.offsetHeight
  ]);

  const allSegments = useMemo(() => {
    return getAllSegments(clipData);
  }, [clipData]);

  const visibleSegments = useMemo(() => {
    if (!isSplitsEnabled) {
      return allSegments;
    }
    return allSegments.filter(segment => segment.id !== 'outro');
  }, [allSegments, isSplitsEnabled]);

  const introSegment = useMemo<VideoSegment | null>(() => {
    return allSegments.find(
      segment => segment.type === SegmentType.VIDEO && segment.id === 'intro'
    ) as VideoSegment | null;
  }, [allSegments]);

  const outroSegment = useMemo<VideoSegment | null>(() => {
    return allSegments.find(
      segment => segment.type === SegmentType.VIDEO && segment.id === 'outro'
    ) as VideoSegment | null;
  }, [allSegments]);

  const splitsIntroSegment = useMemo<VideoSegment | null>(() => {
    if (!isSplitsEnabled) {
      return null;
    }
    return introSegment;
  }, [isSplitsEnabled, introSegment]);

  const splitsOutroSegment = useMemo<VideoSegment | null>(() => {
    if (!isSplitsEnabled) {
      return null;
    }
    return outroSegment;
  }, [isSplitsEnabled, outroSegment]);

  const allCompositeSegments = useMemo(() => {
    return allSegments.filter(segment => segment.type === SegmentType.COMPOSITE);
  }, [allSegments]);

  const {
    segmentTransitionBoundaryMap,
    firstVisibleSegment,
    lastVisibleSegment,
    isStartTransitionApplied,
    isEndTransitionApplied
  } = useMemo(() => {
    return getSegmentTransitionState({
      clipData,
      visibleSegments,
      introSegment: introSegment,
      outroSegment: outroSegment,
      isEnabled: isSegmentTransitionsEnabled
    });
  }, [clipData, visibleSegments, introSegment, outroSegment, isSegmentTransitionsEnabled]);

  return (
    // !! TODO @HiteshChoudhary0098 make this dynamic
    <div
      className={`relative max-w-full px-3 pt-3 ${isDisabled ? 'overflow-x-auto' : 'overflow-x-scroll'}`}
      ref={speakerTimeLineFixedWidthContainer}
    >
      <div className="group/speaker-timeline mb-1 flex w-fit gap-3" ref={speakerTimelineContainerRef}>
        {!splitsIntroSegment && <IntroOutroPopover style={introOutroPopoverStyle} type={AddSegmentType.INTRO} />}
        <div
          className="group/speaker-timeline-container relative z-10 flex h-full w-fit cursor-pointer flex-col gap-1"
          onClick={handleTimelineClick}
        >
          {isChaptersEnabled && !isChaptersInMenuEnabled && (
            <TimelineChapter
              chaptersContainerRef={chaptersContainerRef}
              compositeSegment={allCompositeSegments[0]}
              compositeSegmentDragStart={compositeSegmentDragStart}
              compositeSegmentDragEnd={compositeSegmentDragEnd}
              newTimelineContainerRef={newTimelineContainerRef}
              speakerTimelineContainerRef={speakerTimelineContainerRef}
            />
          )}
          {hasElements && (
            <div ref={timelineSubTrackRef}>
              <TimelineTrackElementLanes />
            </div>
          )}
          <div className="relative flex h-full" ref={speakerTimelineRef} data-testid="speaker-timeline-main">
            {!splitsIntroSegment && <IntroOutroSegment type={AddSegmentType.INTRO} />}
            <SortableContext
              items={visibleSegments.map(segment => segment.id)}
              strategy={horizontalListSortingStrategy}
            >
              {isSegmentTransitionsEnabled && firstVisibleSegment && !introSegment && !isTranslated && (
                <SegmentGapActions
                  previousSegment={introSegment ?? undefined}
                  nextSegment={firstVisibleSegment}
                  isTransitionApplied={isStartTransitionApplied}
                  showAddSegmentRow={!!isSplitsEnabled && !isDraggingTimelineEntity}
                  isSegmentTransitionsEnabled
                  allowInsertWithoutAnchor
                />
              )}
              {visibleSegments.map((segment, index) => {
                const previousSegment = index > 0 ? visibleSegments[index - 1] : null;
                const boundaryIds = getSegmentTransitionBoundaryIds(previousSegment, segment);
                const isBoundaryTransitionApplied = boundaryIds
                  ? segmentTransitionBoundaryMap.has(getSegmentTransitionBoundaryKey(boundaryIds))
                  : false;
                return (
                  <React.Fragment key={segment.id}>
                    {previousSegment && !isTranslated && (
                      <SegmentGapActions
                        previousSegment={previousSegment}
                        nextSegment={segment}
                        isTransitionApplied={isBoundaryTransitionApplied}
                        showAddSegmentRow={!!isSplitsEnabled && !isDraggingTimelineEntity}
                        isSegmentTransitionsEnabled={!!isSegmentTransitionsEnabled}
                      />
                    )}
                    {segment.type === SegmentType.COMPOSITE ? (
                      <TimelineCompositeSegment
                        compositeTimelineRef={compositeTimelineRef}
                        compositeSegment={segment}
                        setCompositeSegmentDragStart={setCompositeSegmentDragStart}
                        setCompositeSegmentDragEnd={setCompositeSegmentDragEnd}
                        setCompositeSegmentDragId={setCompositeSegmentDragId}
                        compositeSegmentDragStart={
                          compositeSegmentDragId === segment.id ? compositeSegmentDragStart : 0
                        }
                        compositeSegmentDragId={compositeSegmentDragId}
                        compositeSegmentDragEnd={compositeSegmentDragId === segment.id ? compositeSegmentDragEnd : 0}
                        newTimelineContainerRef={newTimelineContainerRef}
                        speakerTimelineContainerRef={speakerTimelineContainerRef}
                      />
                    ) : segment.id === 'intro' && splitsIntroSegment ? (
                      <SplitsIntroOutroSegment
                        segment={splitsIntroSegment}
                        videoElementHeight={videoElementHeight}
                        type="intro"
                        speakerTimelineContainerRef={speakerTimelineContainerRef}
                      />
                    ) : segment.type === SegmentType.VIDEO ? (
                      <VideoSegmentComponent
                        segment={segment}
                        videoElementHeight={videoElementHeight}
                        speakerTimelineContainerRef={speakerTimelineContainerRef}
                      />
                    ) : null}
                  </React.Fragment>
                );
              })}
              {isSegmentTransitionsEnabled && lastVisibleSegment && !isTranslated && (
                <SegmentGapActions
                  previousSegment={lastVisibleSegment}
                  nextSegment={outroSegment ?? undefined}
                  isTransitionApplied={isEndTransitionApplied}
                  showAddSegmentRow={!!isSplitsEnabled && !isDraggingTimelineEntity}
                  isSegmentTransitionsEnabled
                  showTransitionOnlyMenu={!isSplitsFFEnabled}
                />
              )}
            </SortableContext>
            {isSplitsEnabled && (
              <FullTimelineSelect
                speakerTimelineContainerRef={speakerTimelineContainerRef}
                speakerTimelineRef={speakerTimelineRef}
              />
            )}
            {splitsOutroSegment ? (
              <SplitsIntroOutroSegment
                segment={splitsOutroSegment}
                videoElementHeight={videoElementHeight}
                type="outro"
                speakerTimelineContainerRef={speakerTimelineContainerRef}
              />
            ) : (
              <IntroOutroSegment type={AddSegmentType.OUTRO} />
            )}
          </div>
          <HoverPoint speakerTimelineRef={speakerTimelineRef} />
          <CuePoint
            compositeSegmentDragStart={compositeSegmentDragStart}
            compositeSegmentDragEnd={compositeSegmentDragEnd}
            newTimelineContainerRef={newTimelineContainerRef}
            speakerTimelineContainerRef={speakerTimelineContainerRef}
            speakerTimeLineFixedWidthContainer={speakerTimeLineFixedWidthContainer}
          />
        </div>
        {!splitsOutroSegment && <IntroOutroPopover style={introOutroPopoverStyle} type={AddSegmentType.OUTRO} />}
      </div>
    </div>
  );
};

export default memo(SpeakerTimeline);
